import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import {
    Container, CssBaseline, AppBar, Toolbar, Typography, Box, TextField,
    Alert, CircularProgress, Snackbar, Stack, Button, ThemeProvider, IconButton, Tooltip,
    useMediaQuery, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider,
    Tabs, Tab, Paper, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DevicesIcon from '@mui/icons-material/Devices';
import ViewListIcon from '@mui/icons-material/ViewList';
import SettingsIcon from '@mui/icons-material/Settings';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import KeyIcon from '@mui/icons-material/Key';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import { createAppTheme } from './theme';
import ClientList from './ClientList';
import ClientDetails from './ClientDetails';
import ApiKeyDialog from './ApiKeyDialog';
import PartnersPanel from './PartnersPanel';
import UsersPanel from './UsersPanel';
import AdminKeyPanel from './AdminKeyPanel';
import PendingRequestsPanel from './PendingRequestsPanel';
import OTAUpdatePanel from './OTAUpdatePanel';
import { AppHeader } from './components/AppHeader';
import { ThemeContext, ThemeProvider as AppThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import { checkCompatibility, displayCompatibilityStatus, FRONTEND_VERSION } from './compatibility';

// Wrap the entire app with our theme context provider and auth provider
export default function AppWithTheme() {
    return (
        <AppThemeProvider>
            <AuthProvider>
                <App />
            </AuthProvider>
        </AppThemeProvider>
    );
}

function App() {
    // Get theme and auth context
    const { mode, toggleTheme, setMode } = useContext(ThemeContext);
    const { currentUser, apiKey, login, logout, isAdmin, isDev, isPartner, loading: authLoading, error: authError, clearError } = useAuth();

    // Create MUI theme based on current mode
    const theme = React.useMemo(() => createAppTheme(mode), [mode]);

    const [selectedClientId, setSelectedClientId] = useState(null);
    const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(!apiKey); // Open if no key stored initially
    const [appError, setAppError] = useState(''); // For errors not specific to list/details
    const [refreshKey, setRefreshKey] = useState(0); // To trigger refresh in ClientList
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    // New state for active tab
    const [activeTab, setActiveTab] = useState(0);
    // Compatibility check state
    const [compatibilityChecked, setCompatibilityChecked] = useState(false);
    const [compatibilityResult, setCompatibilityResult] = useState(null);
    const [showCompatibilityDialog, setShowCompatibilityDialog] = useState(false);

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Determine which tabs to show based on role
    const tabs = React.useMemo(() => {
        const allTabs = [
            { label: 'Clients', icon: <DevicesIcon />, component: 'clients', roles: ['Admin', 'Dev', 'Partner'] },
            { label: 'OTA Updates', icon: <SystemUpdateIcon />, component: 'otaupdates', roles: ['Admin'] },
            { label: 'Users', icon: <PersonIcon />, component: 'users', roles: ['Admin', 'Dev'] },
            { label: 'Partners', icon: <GroupIcon />, component: 'partners', roles: ['Admin', 'Partner'] },
            { label: 'Admin Key', icon: <KeyIcon />, component: 'adminkey', roles: ['Admin'] },
            { label: 'Pending Requests', icon: <NotificationsIcon />, component: 'pending', roles: ['Admin', 'Partner'] },
        ];

        // Filter tabs based on user role, or show all for legacy admin keys
        if (!currentUser || currentUser.isLegacy) {
            return allTabs; // Show all tabs for legacy admin keys
        }

        return allTabs.filter(tab => tab.roles.includes(currentUser.role));
    }, [currentUser]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Reset selected client when switching away from clients tab
        if (tabs[newValue]?.component !== 'clients') {
            setSelectedClientId(null);
        }
    };

    // Check version compatibility on mount
    useEffect(() => {
        const performCompatibilityCheck = async () => {
            try {
                const result = await checkCompatibility();
                setCompatibilityResult(result);
                displayCompatibilityStatus(result);
                
                // Show dialog if incompatible
                if (result.compatible === false) {
                    setShowCompatibilityDialog(true);
                }
                
                setCompatibilityChecked(true);
            } catch (error) {
                console.error('Compatibility check failed:', error);
                setCompatibilityChecked(true);
            }
        };
        
        performCompatibilityCheck();
    }, []);

    // Force light mode if no API key
    useEffect(() => {
        if (!apiKey && mode !== 'light') {
            setMode('light');
        }
    }, [apiKey, mode, setMode]);

    // Clear app error when API key is removed
    useEffect(() => {
        if (!apiKey) {
            setAppError('');
        }
    }, [apiKey]);

    // Handle authentication errors - reopen dialog if auth fails
    useEffect(() => {
        if (authError && !apiKey) {
            // Auth failed and key was cleared - reopen dialog
            setApiKeyDialogOpen(true);
            // Clear any background error banner - error should only show in dialog
            setAppError('');
        } else if (currentUser && apiKey && !authLoading && apiKeyDialogOpen) {
            // Auth succeeded while dialog was open - close dialog and switch to dark mode
            setApiKeyDialogOpen(false);
            if (mode === 'light') {
                setMode('dark');
            }
            // Show success message
            setSnackbarMessage('Logged in successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        }
    }, [authError, apiKey, currentUser, authLoading, apiKeyDialogOpen, mode, setMode]);

    const handleClientSelect = useCallback((clientId) => {
        setSelectedClientId(clientId);
        setDrawerOpen(false); // Close drawer when selecting a client on mobile
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedClientId(null);
    }, []);

    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1); // Increment key to trigger refresh
        setSelectedClientId(null); // Deselect client on refresh

        // Show snackbar notification
        setSnackbarMessage('Refreshing client list...');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
    }, []);

    const handleApiKeySave = (newKey) => {
        login(newKey); // Use auth context login
        // Don't close dialog yet - wait for auth validation
        // The dialog will close automatically when currentUser is set (via useEffect)
        setRefreshKey(prev => prev + 1); // Refresh list after getting key
    };

    const handleLogout = () => {
        logout();
        setApiKeyDialogOpen(true);
        setActiveTab(0);
        setSelectedClientId(null);
        setSnackbarMessage('Logged out successfully');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
    };

    const handleActionSuccess = useCallback((message) => {
        // Display success notification for actions
        setSnackbarMessage(message || 'Action completed successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setRefreshKey(prev => prev + 1); // Refresh client list
    }, []);

    const handleAppError = useCallback((error) => {
        if (!error) {
            setAppError('');
            return;
        }

        setAppError(error);
        // Also show in snackbar
        setSnackbarMessage(`Error: ${error}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
    }, []);

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const drawer = (
        <Box sx={{ width: 250 }} role="presentation">
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    WobotRelay Dashboard
                </Typography>
                {currentUser && !currentUser.isLegacy && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {currentUser.name}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Divider />
            <List>
                {tabs.map((tab, index) => (
                    <ListItem
                        button
                        key={tab.component}
                        onClick={() => { setActiveTab(index); setDrawerOpen(false); }}
                        selected={activeTab === index}
                    >
                        <ListItemIcon>{tab.icon}</ListItemIcon>
                        <ListItemText primary={tab.label} />
                    </ListItem>
                ))}
                <Divider />
                <ListItem button onClick={handleRefresh}>
                    <ListItemIcon>
                        <RefreshIcon />
                    </ListItemIcon>
                    <ListItemText primary="Refresh" />
                </ListItem>
                <ListItem button onClick={() => setApiKeyDialogOpen(true)}>
                    <ListItemIcon>
                        <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText primary="API Key Settings" />
                </ListItem>
                <ListItem button onClick={toggleTheme}>
                    <ListItemIcon>
                        {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                    </ListItemIcon>
                    <ListItemText primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} />
                </ListItem>
                <ListItem button onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                </ListItem>
            </List>
        </Box>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <AppBar
                    position="static"
                    sx={{
                        background: (theme) =>
                            theme.palette.mode === 'dark'
                                ? 'linear-gradient(45deg, #283593 30%, #6a1b9a 90%)'
                                : 'linear-gradient(45deg, #2196f3 30%, #e91e63 90%)',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <Toolbar>
                        {isSmallScreen && (
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer}
                                sx={{ mr: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DevicesIcon sx={{ mr: 1 }} />
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                WobotRelay Dashboard
                            </Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />

                        {!isSmallScreen && apiKey && (
                            <>
                                {currentUser && !currentUser.isLegacy && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                mr: 1,
                                                bgcolor: currentUser.role === 'Admin' ? 'error.main' :
                                                    currentUser.role === 'Dev' ? 'warning.main' : 'info.main'
                                            }}
                                        >
                                            {currentUser.name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                                {currentUser.name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                                    <IconButton
                                        color="inherit"
                                        onClick={toggleTheme}
                                        sx={{ mr: 1 }}
                                    >
                                        {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    color="inherit"
                                    onClick={handleRefresh}
                                    startIcon={<RefreshIcon />}
                                    sx={{ mr: 1 }}
                                >
                                    Refresh
                                </Button>
                                <Tooltip title="Logout">
                                    <IconButton
                                        color="inherit"
                                        onClick={handleLogout}
                                    >
                                        <LogoutIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Toolbar>
                </AppBar>

                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                >
                    {drawer}
                </Drawer>

                <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
                    {appError && !apiKeyDialogOpen && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => setAppError('')}
                        >
                            {appError}
                        </Alert>
                    )}

                    <ApiKeyDialog
                        open={apiKeyDialogOpen}
                        currentKey={apiKey}
                        onSave={handleApiKeySave}
                        onClose={() => { 
                            if (apiKey && currentUser) {
                                setApiKeyDialogOpen(false);
                                clearError();
                            }
                        }} // Only close if authenticated
                        loading={authLoading}
                        error={authError}
                        onClearError={clearError}
                    />

                    {!apiKey && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '60vh'
                        }}>
                            <SecurityIcon sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
                            <Typography variant="h5" align="center" color="text.secondary" gutterBottom>
                                API Key Required
                            </Typography>
                            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
                                Please set the API Key to interact with the server.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => setApiKeyDialogOpen(true)}
                                startIcon={<SecurityIcon />}
                            >
                                Set API Key
                            </Button>
                        </Box>
                    )}

                    {apiKey && (authLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Navigation Tabs */}
                            <Paper sx={{ mb: 3 }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    variant={isSmallScreen ? "scrollable" : "fullWidth"}
                                    scrollButtons={isSmallScreen ? "auto" : false}
                                    centered={!isSmallScreen}
                                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                    {tabs.map((tab) => (
                                        <Tab
                                            key={tab.component}
                                            icon={tab.icon}
                                            iconPosition="start"
                                            label={tab.label}
                                        />
                                    ))}
                                </Tabs>
                            </Paper>

                            {/* Tab Panels - Dynamically render based on tabs array */}
                            {tabs.map((tab, index) => (
                                <Box key={tab.component} role="tabpanel" hidden={activeTab !== index}>
                                    {activeTab === index && (
                                        <>
                                            {tab.component === 'clients' && (
                                                <Stack spacing={3}>
                                                    <ClientList
                                                        apiKey={apiKey}
                                                        onClientSelect={handleClientSelect}
                                                        onRefresh={handleRefresh}
                                                        refreshKey={refreshKey}
                                                        onError={handleAppError}
                                                    />
                                                    {selectedClientId && (
                                                        <div id="client-details-section">
                                                            <ClientDetails
                                                                apiKey={apiKey}
                                                                clientId={selectedClientId}
                                                                onClose={handleCloseDetails}
                                                                onActionSuccess={handleActionSuccess}
                                                            />
                                                        </div>
                                                    )}
                                                </Stack>
                                            )}
                                            {tab.component === 'otaupdates' && (
                                                <OTAUpdatePanel
                                                    apiKey={apiKey}
                                                    onError={handleAppError}
                                                />
                                            )}
                                            {tab.component === 'users' && (
                                                <UsersPanel
                                                    apiKey={apiKey}
                                                    onError={handleAppError}
                                                />
                                            )}
                                            {tab.component === 'partners' && (
                                                <PartnersPanel
                                                    apiKey={apiKey}
                                                    onError={handleAppError}
                                                />
                                            )}
                                            {tab.component === 'adminkey' && (
                                                <AdminKeyPanel
                                                    apiKey={apiKey}
                                                    onError={handleAppError}
                                                />
                                            )}
                                            {tab.component === 'pending' && (
                                                <PendingRequestsPanel
                                                    apiKey={apiKey}
                                                    onError={handleAppError}
                                                />
                                            )}
                                        </>
                                    )}
                                </Box>
                            ))}
                        </>
                    ))}
                </Container>

                <Box component="footer" sx={{
                    p: 2,
                    mt: 'auto',
                    backgroundColor: mode === 'dark' ? 'background.paper' : 'background.paper',
                    borderTop: 1,
                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                    textAlign: 'center'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        WobotRelay • Device Management System
                    </Typography>
                </Box>
            </Box>

            {/* Global snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Compatibility Warning Dialog */}
            <Dialog
                open={showCompatibilityDialog}
                onClose={() => setShowCompatibilityDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" />
                    Version Compatibility Warning
                </DialogTitle>
                <DialogContent>
                    {compatibilityResult && (
                        <Stack spacing={2}>
                            <Alert severity={compatibilityResult.severity || 'error'}>
                                {compatibilityResult.reason}
                            </Alert>
                            
                            {compatibilityResult.details && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Version Information:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        • Frontend Version: {FRONTEND_VERSION}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        • Backend Version: {compatibilityResult.details.backend.version}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        • Backend API Version: {compatibilityResult.details.backend.apiVersion}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        • Required Frontend Range: {compatibilityResult.details.supports.frontend.min} to {compatibilityResult.details.supports.frontend.max}
                                    </Typography>
                                </Box>
                            )}
                            
                            {compatibilityResult.action === 'upgrade' && (
                                <Alert severity="info">
                                    Please upgrade your frontend to continue using this application.
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCompatibilityDialog(false)} color="primary">
                        I Understand
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}